import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendUserInfo,
	FrontendUserLicense,
} from "node-library/dist/portal";
import {
	CommonSwitch,
	CustomRadioGroup,
	CustomTextInput,
	UserInfoTextInput,
} from "../../common-components/inputs";
import { Card, Divider } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import {
	CompanyIcon,
	DeleteIcon,
	InvoiceIcon,
	LockIcon,
	UserIcon,
} from "../../common-components/svgIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { FrontendProfileTab, Severity } from "node-library/dist/constants";
import { YellowWarnIcon } from "../../common-components/toast/warnIcons";
import { BlueButton, TextButton } from "../../common-components/button";
import AddIcon from "@mui/icons-material/Add";

import CopyEmailSection from "../mainPages/common/copyEmailSection";
import ChangePasswordModal from "./components/ChangePasswordModal";
import { sendGETRequest } from "../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import UserClientInfoSection from "./userClientInfoSection";
import CountryProvinceSelector from "../../common-components/countryAndProvinceSelector";

interface Props {
	billingInformationFields1: Array<any>;
	billingInformationFields2: Array<any>;
	billingInformationFields3: Array<any>;
	defaultBillingCountry: string;
	setDefaultBillingCountry: React.Dispatch<React.SetStateAction<string>>;
	defaultBillingProvince: string;
	setDefaultBillingProvince: React.Dispatch<React.SetStateAction<string>>;
	defaultUseSameBillingInfo: boolean;
	setDefaultUseSameBillingInfo: React.Dispatch<React.SetStateAction<boolean>>;
	billingInfoEmails: Array<string>;
	setBillingInfoEmails: React.Dispatch<React.SetStateAction<Array<string>>>;
	billingInfoEmailsErrorMsgs: Array<string>;
	setBillingInfoEmailsErrorMsgs: React.Dispatch<
		React.SetStateAction<Array<string>>
	>;

	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function UserBillingInfoSection(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		billingInformationFields1,
		billingInformationFields2,
		billingInformationFields3,
		defaultBillingCountry,
		setDefaultBillingCountry,
		defaultBillingProvince,
		setDefaultBillingProvince,
		billingInfoEmails,
		setBillingInfoEmails,
		billingInfoEmailsErrorMsgs,
		setBillingInfoEmailsErrorMsgs,
		defaultUseSameBillingInfo,
		setDefaultUseSameBillingInfo,
		setUnsavedChanges,
	} = props;

	function newBillingInfoEmail() {
		if (billingInfoEmails?.length >= 5) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("EmailLimitReachedDescription"),
				<YellowWarnIcon />
			);
			return;
		}
		setBillingInfoEmails((prevItems) => [...prevItems, ""]);
		setBillingInfoEmailsErrorMsgs((prevItems) => [...prevItems, ""]);
		setUnsavedChanges(true);
	}
	function removeBillingInfoEmail(index: number) {
		if (index >= 0 && index < billingInfoEmailItems.length) {
			setBillingInfoEmails((prevItems) =>
				prevItems.filter((_, i) => i !== index)
			);
			setBillingInfoEmailsErrorMsgs((prevItems) =>
				prevItems.filter((_, i) => i !== index)
			);
			setUnsavedChanges(true);
		} else {
			return;
		}
	}
	function updateBillingInfoEmail(index: number, newItem: string) {
		// Create a new array with the updated item at the specific index
		const updatedEmails = billingInfoEmails.map(
			(email, i) => (i === index ? newItem : email) // If the index matches, replace the item
		);

		// Update the state with the new array
		setBillingInfoEmails(updatedEmails);
		setUnsavedChanges(true);
	}

	const billingInfoEmailItems: Array<React.ReactNode> = billingInfoEmails.map(
		(email, index) => {
			return (
				<div
					key={index}
					style={{ display: "flex", gap: "10px", alignItems: "center" }}
				>
					<div style={{ width: "100%" }}>
						<CustomTextInput
							id={`billing-email-input-${index}`}
							key={index}
							value={email}
							setValue={(value) => {
								updateBillingInfoEmail(index, value);
							}}
							placeholder={translation("EmailAddress")}
							required
							errorMsg={billingInfoEmailsErrorMsgs[index]}
							setErrorMsg={(msg: string) => {
								const updatedMsgs = billingInfoEmailsErrorMsgs.map(
									(txt, i) => (i === index ? msg : txt) // If the index matches, replace the item
								);

								// Update the state with the new array
								setBillingInfoEmailsErrorMsgs(updatedMsgs);
							}}
						/>
					</div>
					<div
						id={`delete-billing-email-input-${index}`}
						style={{ cursor: "pointer" }}
						onClick={() => {
							setUnsavedChanges(true);
							removeBillingInfoEmail(index);
						}}
					>
						<DeleteIcon></DeleteIcon>
					</div>
				</div>
			);
		}
	);

	const billingInformation: React.ReactNode = (
		<div>
			<div className="profile-information-container">
				<div className="profile-icon-header-row typography-heading-03">
					<InvoiceIcon
						strokeColor="#FFFFFF"
						fillColor={NeutralColor.NEUTRAL_GREY_900}
					></InvoiceIcon>
					{translation("BillingInformation")}
				</div>
				<div
					className="typography-caption"
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
				>
					{translation("ProfileBillingInformationDescription")}
				</div>
				<div className="profile-billing-info-same-info-switch">
					<div className="typography-body-01">
						{translation("UseCompanyInformationForBilling")}
					</div>
					<CommonSwitch
						id="company-info-for-billing-switch"
						checked={defaultUseSameBillingInfo}
						setChecked={(checked: boolean) => {
							setDefaultUseSameBillingInfo(checked);
							setUnsavedChanges(true);
						}}
					/>
				</div>
			</div>

			<div
				style={{
					display: defaultUseSameBillingInfo ? "none" : "initial",
				}}
			>
				<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>

				<div className="profile-company-info-input-row">
					{billingInformationFields1.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
				<div className="profile-client-info-input-row">
					{billingInformationFields2.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
					<CountryProvinceSelector
						countryDropdownId={"default-billing-country-input"}
						provinceDropdownId={"default-billing-province-input"}
						selectedCountryName={defaultBillingCountry}
						setSelectedCountryName={setDefaultBillingCountry}
						selectedState={defaultBillingProvince}
						setSelectedState={setDefaultBillingProvince}
						setUnsavedChanges={setUnsavedChanges}
					></CountryProvinceSelector>
				</div>
				<div className="profile-company-info-input-row">
					{billingInformationFields3.map((field) => {
						return (
							<CustomTextInput
								id={field.id}
								key={field.id}
								value={field.value}
								setValue={(value) => {
									field.setValue(value);
									setUnsavedChanges(true);
								}}
								label={field.label}
								disabled={field.disabled}
								type={field.type}
								required={field.required}
								errorMsg={field.errorMsg}
								setErrorMsg={field.setErrorMsg}
							></CustomTextInput>
						);
					})}
				</div>
			</div>
			<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>
			<div className="profile-page-copy-email-container">
				<CopyEmailSection
					title={translation("DefaultBillingInformationCopiesTo")}
					// customHeaderClass="profile-default-email-header"
					button={
						<TextButton
							id={"add-billing-email-button"}
							onClick={() => {
								newBillingInfoEmail();
							}}
							size="xsmall"
						>
							<AddIcon style={{ fontSize: 20 }} />
							<span className="typography-body-02">{translation("Email")}</span>
						</TextButton>
					}
					items={billingInfoEmailItems}
				></CopyEmailSection>
			</div>
		</div>
	);

	return billingInformation;
}
